import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiSearch,
  FiPlusCircle,
  FiMinusCircle,
  FiGrid,
  FiList,
  FiRefreshCw
} from "react-icons/fi";
import { Button, Spin, notification } from "antd";
import PropertyCard from "./PropertyCard";
import PropertyTable from "./PropertyTable";
import NewPropertyForm from "./properties/NewPropertyForm";
import EditPropertyForm from "./properties/EditPropertyForm";
import DateTimeModal from "./DateTimeModal";
import propertiesService from "../services/propertiesService";
import cartService from "../services/cartService";
import userRoleUtils from "../utils/userRoleUtils";
import eventBus from "../utils/eventBus";

const Properties = ({ properties, data, refreshProperties, setCartItems, removeFromCart, shedule }) => {
  const PAGE_SIZE = 48;
  const [searchTerm, setSearchTerm] = useState("");
  const [showNewPropertyForm, setShowNewPropertyForm] = useState(false);
  const [editingPropertyId, setEditingPropertyId] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [cart, setCart] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateTimeModalVisible, setDateTimeModalVisible] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [localProperties, setLocalProperties] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const scrollContainerRef = useRef(null);
  const loadMoreRef = useRef(null);

  // Initialize state from props and setup event listeners
  useEffect(() => {
    if (properties && properties.length > 0) {
      setLocalProperties(properties);
      setFilteredProperties(properties);
    } else {
      setLocalProperties([]);
      setFilteredProperties([]);
    }
    
    setCart(data?.cart || {});
    
    // Subscribe to cart updates
    const unsubscribeCart = eventBus.on('cart:updated', (updatedCart) => {
      setCart(updatedCart || {});
    });
    
    // Subscribe to property updates
    const unsubscribeProperty = eventBus.on('property:updated', (updatedProperty) => {
      setLocalProperties(prev => 
        prev.map(prop => prop.id === updatedProperty.id ? updatedProperty : prop)
      );
    });
    
    return () => {
      unsubscribeCart();
      unsubscribeProperty();
    };
  }, [properties, data?.cart]);

  // Filter properties when search term changes
  useEffect(() => {
    if (localProperties && localProperties.length > 0) {
      const filtered = localProperties.filter(
        (property) =>
          property?.property_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          property?.apartment_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          property?.postcode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          property?.delivery_zip_code?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProperties(filtered);
    } else {
      setFilteredProperties([]);
    }
  }, [searchTerm, localProperties]);

  // Reset visible window when dataset/search/view changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [PAGE_SIZE, searchTerm, viewMode, localProperties?.length]);

  const displayedProperties = useMemo(() => {
    return (filteredProperties || []).slice(0, visibleCount);
  }, [filteredProperties, visibleCount]);

  const hasMore = visibleCount < (filteredProperties?.length || 0);

  // Instagram-like infinite scroll inside the scrollable container
  useEffect(() => {
    const root = scrollContainerRef.current;
    const target = loadMoreRef.current;
    if (!root || !target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (!hasMore) return;
        setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filteredProperties.length));
      },
      { root, rootMargin: "300px 0px", threshold: 0 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [PAGE_SIZE, filteredProperties?.length, hasMore]);

  const refreshCart = async () => {
    try {
      const response = await cartService.getCart();
      if (response && response.cart) {
        setCart(response.cart);
        if (setCartItems) {
          setCartItems(response.cart);
        }
        // Emit a cart:updated event
        eventBus.emit('cart:updated', response.cart);
      }
    } catch (error) {
      console.error("Error refreshing cart:", error);
    }
  };

  const handleEdit = (propertyId) => {
    setEditingPropertyId(propertyId);
  };

  const handleDelete = async (propertyId) => {
    try {
      const response = await propertiesService.deleteProperty(propertyId);
      if (response.status) {
        notification.success({
          message: "Successful",
          description: "Property deleted successfully!",
        });
        
        // Update local state immediately
        setLocalProperties(prev => prev.filter(prop => prop.id !== propertyId));
        
        // Also update cart if the property was in the cart
        refreshCart();
        
        // FIXED: Emit property deleted event and refresh parent properties
        eventBus.emit('property:deleted', propertyId);
        
        // Call parent refresh if available
        if (refreshProperties) {
          await refreshProperties();
        }
        
        // Force refresh the local data as well
        await forcedRefreshProperties();
        
      } else {
        notification.error({
          message: "Failed to delete property!",
          description: response?.message,
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while deleting the property.",
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingPropertyId(null);
  };

  const handleUpdateProperty = async (updatedProperty) => {
    // Update local state immediately
    setLocalProperties(prev => 
      prev.map(prop => prop.id === updatedProperty.id ? updatedProperty : prop)
    );
    
    notification.success({
      message: "Successful",
      description: "Property updated successfully!",
    });
    
    setEditingPropertyId(null);
    
    // Emit a property:updated event
    eventBus.emit('property:updated', updatedProperty);
    
    // FIXED: Also refresh parent properties
    if (refreshProperties) {
      await refreshProperties();
    }
  };

  const handleSaveProperty = async (formData) => {
    try {
      let response = await propertiesService.createProperty(formData);
      if (response.status) {
        notification.success({
          message: "Successful",
          description: `${response.message}!`,
        });
        
        // FIXED: Refresh parent properties first
        if (refreshProperties) {
          await refreshProperties();
        }
        
        // Then refresh local properties
        await forcedRefreshProperties();
        
        setShowNewPropertyForm(false);
        
        // Emit property created event
        eventBus.emit('property:created', response);
        
      } else {
        notification.error({
          message: "Failed to save property!",
          description: response?.message || "An error occurred while saving the property.",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while saving the property.",
      });
    }
  };

  const forcedRefreshProperties = async () => {
    try {
      setLoading(true);
      const response = await propertiesService.getAllProperties();
      
      if (response && response.properties) {
        setLocalProperties(response.properties);
        setVisibleCount(PAGE_SIZE);
        
        if (response.order_schedule) {
          // Update local storage for schedules
          const existingSchedules = JSON.parse(localStorage.getItem('dateIsSet') || '{}');
          const updatedSchedules = { ...existingSchedules };
          
          // Add any new schedules from the response
          Object.entries(response.order_schedule).forEach(([propertyId, schedule]) => {
            updatedSchedules[propertyId] = {
              order_date: schedule.order_date,
              arrival_time: schedule.arrival_time,
              completion_time: schedule.completion_time
            };
          });
          
          // Save updated schedules to localStorage
          localStorage.setItem('dateIsSet', JSON.stringify(updatedSchedules));
          
          // Emit schedule updated event
          eventBus.emit('schedule:updated', updatedSchedules);
        }
        
        // Refresh cart
        refreshCart();
        
        notification.success({
          message: 'Properties Refreshed',
          description: 'Properties list has been updated successfully.'
        });
      }
    } catch (error) {
      console.error('Error refreshing properties:', error);
      notification.error({
        message: 'Refresh Failed',
        description: 'Failed to refresh properties. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditDateTime = (property) => {
    setSelectedProperty(property);
    setDateTimeModalVisible(true);
  };

  const handleDateTimeModalClose = () => {
    setDateTimeModalVisible(false);
    setSelectedProperty(null);
  };

  const handleDateTimeSuccess = (schedule) => {
    // Update the selected property locally with the new date and time
    if (selectedProperty && schedule) {
      const updatedProperty = {
        ...selectedProperty,
        date: schedule.order_date,
        arrival_time: schedule.arrival_time,
        completion_time: schedule.completion_time
      };
      
      // Update the local state
      setLocalProperties(prev => 
        prev.map(prop => prop.id === selectedProperty.id ? updatedProperty : prop)
      );
      
      // Emit a property:updated event
      eventBus.emit('property:updated', updatedProperty);
    }
    
    setDateTimeModalVisible(false);
    setSelectedProperty(null);
    
    // Refresh cart to ensure date time changes are reflected
    refreshCart();
  };

  // Get minimum date allowed for orders
  const getMinimumDate = () => {
    return userRoleUtils.getMinimumOrderDate();
  };

  const addToFromCart = async (property) => {
    try {
      setLoading(true);
      const dataToSend = new FormData();

      // Check if property.date is less than getMinimumDate if yes set minimumdate
      // Only for regular users, not for admins or impersonated users
      let propertyDate = property.date;
      if(!userRoleUtils.isAdminOrImpersonating() && new Date(property.date) < new Date(getMinimumDate())) {
        propertyDate = getMinimumDate();
      }

      const propertyData = {
        property_id: property.id.toString(),
        property_title: property.property_title,
        property_type: property.property_type_id.toString(),
        property_description: property.property_description,
        contact_name: property.contact_person,
        cleaner_information: property.cleaner_information,
        contact_number: property.contact_number,
        contact_description: property.contact_description || "",
        arrival_times: property.arrival_time
          ? property.arrival_time.split(":")
          : ["", ""],
        completion_times: property.completion_time
          ? property.completion_time.split(":")
          : ["", ""],
        date: propertyDate,
        quantity: JSON.parse(property.quantity || "{}"),
        appartment_address: property.apartment_address,
        appartment_number: property.apartment_number,
        delivery_city: property.delivery_city,
        delivery_country: property.delivery_country,
        post_code: property.delivery_zip_code,
        product_quantity: JSON.parse(property.product_quantity || "{}")
      };

      dataToSend.append("type", 3);

      // Add basic fields
      Object.entries(propertyData).forEach(([key, value]) => {
          if (
              ![
                  "quantity",
                  "product_quantity",
                  "arrival_times",
                  "completion_times",
              ].includes(key)
          ) {
              dataToSend.append(key, value);
          }
      });

      // Add arrival and completion times
      propertyData.arrival_times?.forEach((time) =>
          dataToSend.append("arrival_times[]", time)
      );
      propertyData.completion_times?.forEach((time) =>
          dataToSend.append("completion_times[]", time)
      );

      // Add quantity
      Object.entries(propertyData.quantity || {}).forEach(([roomId, count]) => {
          dataToSend.append(`quantity[${roomId}]`, count);
      });

      // Add product_quantity
      Object.entries(propertyData.product_quantity || {}).forEach(
          ([productId, product]) => {
              Object.entries(product).forEach(([field, value]) => {
                  dataToSend.append(
                      `product_quantity[${productId}][${field}]`,
                      value
                  );
              });
          }
      );

      const response = await propertiesService.updateProperty(dataToSend);
      if (response && response.status) {
        // If API returns the cart, use it directly
        if (response.cart) {
          setCart(response.cart);
          if (setCartItems) {
            setCartItems(response.cart);
          }
          
          // Emit cart updated event
          eventBus.emit('cart:updated', response.cart);
        } else {
          // Otherwise refresh cart
          await refreshCart();
        }
        
        notification.success({
          message: "Success",
          description: "Property added to cart successfully!",
          duration: 2
        });
        
        return true;
      } else {
        throw new Error(response?.message || "Failed to add to cart");
      }
    } catch (error) {
      console.error("Error adding property to cart:", error);
      notification.error({
        message: "Failed!",
        description: error.message || "An error occurred while adding to cart",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Enhanced method for handling property status toggle
  const handleToggleStatus = async (propertyId, newStatus) => {
    try {
      const response = await propertiesService.updatePropertyStatus(propertyId, newStatus);
      if (response) {
        notification.success({
          message: "Status Updated",
          description: newStatus === 1 ? "The property successful Activated." : "The property successfully Inactived.",
        });
        
        // Update local state
        setLocalProperties(prev => 
          prev.map(prop => prop.id === propertyId ? {...prop, status: newStatus} : prop)
        );
        
        // FIXED: Emit status change event and refresh parent properties
        eventBus.emit('property:statusChanged', { propertyId, status: newStatus });
        
        if (refreshProperties) {
          await refreshProperties();
        }
        
        // Also refresh cart in case the toggled property was in the cart
        refreshCart();
      } else {
        notification.error({
          message: "Update Failed",
          description: response.message || "Failed to update property status.",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while updating property status.",
      });
    }
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-center mb-5">
        <h2 className="text-xl sm:text-2xl font-bold text-golden-navy flex items-center min-w-0">
          <img
            src="/ico/MyProperties.svg"
            alt="My Properties Icon"
            className="inline-block mr-2 h-7 sm:h-8 flex-shrink-0"
          />
          <span className="truncate">
            {showNewPropertyForm
              ? "New Property Info"
              : editingPropertyId
              ? "Edit Property"
              : `My Properties (${filteredProperties?.length})`}
          </span>
        </h2>
        
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap lg:w-auto lg:justify-end">
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="Search Property"
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-golden-blue focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FiSearch className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
          
          <Button 
            icon={<FiRefreshCw />} 
            onClick={forcedRefreshProperties}
            loading={loading}
            className="w-full sm:w-auto"
          >
            Refresh Properties
          </Button>
          
          {/* View Toggle Buttons */}
          <div className="flex w-full sm:w-auto bg-white rounded-lg shadow-sm">
            <button
              className={`flex-1 sm:flex-none px-4 py-2 flex items-center justify-center rounded-l-lg transition-colors ${
                viewMode === "grid"
                  ? "bg-golden-navy text-white"
                  : "text-gray-500 hover:text-golden-navy"
              }`}
              onClick={() => setViewMode("grid")}
            >
              <FiGrid className="h-5 w-5 mr-2" />
              Grid
            </button>
            <button
              className={`flex-1 sm:flex-none px-4 py-2 flex items-center justify-center rounded-r-lg transition-colors ${
                viewMode === "list"
                  ? "bg-golden-navy text-white"
                  : "text-gray-500 hover:text-golden-navy"
              }`}
              onClick={() => setViewMode("list")}
            >
              <FiList className="h-5 w-5 mr-2" />
              List
            </button>
          </div>

          <button
            className="flex w-full sm:w-auto items-center justify-center bg-golden-navy text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition-colors duration-300"
            onClick={() => {
              if (editingPropertyId) {
                setEditingPropertyId(null);
              } else {
                setShowNewPropertyForm(!showNewPropertyForm);
              }
            }}
          >
            {!showNewPropertyForm && !editingPropertyId ? (
              <FiPlusCircle className="h-5 w-5 mr-2" />
            ) : (
              <FiMinusCircle className="h-5 w-5 mr-2" />
            )}
            {(showNewPropertyForm || editingPropertyId) ? "Cancel" : "Add New Property"}
          </button>
        </div>
      </div>

      {showNewPropertyForm ? (
        <NewPropertyForm onSave={handleSaveProperty} />
      ) : editingPropertyId ? (
        <EditPropertyForm
          propertyId={editingPropertyId}
          onSave={handleUpdateProperty}
          onCancel={handleCancelEdit}
        />
      ) : (
        <div className="w-full">
          {viewMode === "grid" ? (
            <div
              style={{ maxHeight: "75vh" }}
              ref={scrollContainerRef}
              className="rounded-lg overflow-y-auto px-1 sm:px-3 pt-2 sm:pt-3 grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3"
            >
              {displayedProperties.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  cart={cart}
                  shedule={shedule}
                  addToFromCart={() => addToFromCart(property)}
                  removeFromCart={(pid) => {
                    if (removeFromCart) {
                      removeFromCart(pid);
                      // After removing, refresh the cart data
                      setTimeout(refreshCart, 300);
                    }
                  }}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  onEditDateTime={() => handleEditDateTime(property)}
                  onToggleStatus={handleToggleStatus}
                />
              ))}
              <div ref={loadMoreRef} style={{ height: 1, width: "100%" }} />
            </div>
          ) : (
            <div
              style={{ maxHeight: "75vh" }}
              className="overflow-y-auto"
              ref={scrollContainerRef}
            >
              <PropertyTable
                properties={displayedProperties}
                cart={cart}
                shedule={shedule}
                addToFromCart={addToFromCart}
                removeFromCart={(pid) => {
                  if (removeFromCart) {
                    removeFromCart(pid);
                    // After removing, refresh the cart data
                    setTimeout(refreshCart, 300);
                  }
                }}
                onEdit={handleEdit}
                onEditDateTime={handleEditDateTime}
                onToggleStatus={handleToggleStatus}
              />
              <div ref={loadMoreRef} style={{ height: 1 }} />
            </div>
          )}

          {/* Load-more footer */}
          {!showNewPropertyForm && !editingPropertyId && (
            <div className="mt-3 flex justify-center items-center">
              {loading ? (
                <Spin />
              ) : hasMore ? (
                <Button onClick={() => setVisibleCount((v) => Math.min(v + PAGE_SIZE, filteredProperties.length))}>
                  Load more
                </Button>
              ) : (
                <span className="text-gray-500">
                  Showing {Math.min(visibleCount, filteredProperties?.length || 0)} of {filteredProperties?.length || 0}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {filteredProperties?.length === 0 && !showNewPropertyForm && !editingPropertyId && (
        <div className="text-center text-gray-500 mt-6">
          No properties found matching your search.
        </div>
      )}

      {/* Date Time Modal */}
      {dateTimeModalVisible && selectedProperty && (
        <DateTimeModal
          isOpen={dateTimeModalVisible}
          onClose={handleDateTimeModalClose}
          propertyId={selectedProperty.id}
          initialData={{
            arrival_time: selectedProperty.arrival_time,
            completion_time: selectedProperty.completion_time,
            order_date: selectedProperty.date
          }}
          onSave={handleDateTimeSuccess}
        />
      )}
    </div>
  );
};

export default Properties;
